import { request } from '@umijs/max';
import { registerWsHandler, WsEventType, onSocketReconnected } from '../websocket';

/**
 * Display label + color for a callback's origin (the `source` column), or null
 * when unknown/legacy so callers can render nothing.
 */
export function callbackSourceTag(source?: string | null): { label: string; color: string } | null {
  switch ((source || '').toLowerCase()) {
    case 'dispatch_butler':
      return { label: 'Dispatch', color: 'geekblue' };
    case 'appt_center':
      return { label: 'Appt Center', color: 'purple' };
    default:
      return null;
  }
}

// ─── Callback-queue event bus (mirrors the CSR-prompt WS pattern in csr-activity.ts) ──
const CALLBACK_QUEUE_UPDATED_EVENT = 'callbackQueueUpdated';

/** The pending snapshot a "call-queue-updated" WS push carries — see
 * CallQueueAssignment::broadcastPending() on the backend. Assignment shape matches
 * QueueItem (frontend/src/contexts/CallbackQueueContext.tsx). */
export type CallbackQueueUpdatedPayload = {
  pending_count: number;
  assignments: any[];
};

/**
 * `data` carries the pending snapshot straight off the WS push, so a listener can
 * update state directly instead of turning around and re-fetching over REST. This
 * board is shared/tenant-wide (not per-user), so broadcasting the full row data to
 * every connected user is fine — unlike per-user notification pushes.
 */
export function emitCallbackQueueUpdated(data?: CallbackQueueUpdatedPayload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<CallbackQueueUpdatedPayload | undefined>(CALLBACK_QUEUE_UPDATED_EVENT, { detail: data }));
}

/** Subscribe to queue pushes; returns an unsubscribe function. */
export function onCallbackQueueUpdated(handler: (data?: CallbackQueueUpdatedPayload) => void) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<CallbackQueueUpdatedPayload | undefined>).detail);
  window.addEventListener(CALLBACK_QUEUE_UPDATED_EVENT, listener);
  return () => window.removeEventListener(CALLBACK_QUEUE_UPDATED_EVENT, listener);
}

// Coalesce a burst of pushes (e.g. several assignments changing in the same sync run)
// into one apply — safe because each push already carries the full pending snapshot,
// so only the latest one need ever reach the listener.
registerWsHandler(WsEventType.CALL_QUEUE_UPDATED, emitCallbackQueueUpdated, { coalesceMs: 300 });

// A dropped push during a disconnect never arrives on its own — resync the
// instant the socket comes back instead of waiting out the 20s poll fallback.
// No payload rides along (same as an older-server push), so the listener in
// CallbackQueueContext falls back to a REST re-fetch.
onSocketReconnected(() => emitCallbackQueueUpdated());

/** GET /api/call-queue/assignments?status=pending on our backend — returns the pending count. */
export async function getPendingCallbackCount(): Promise<number> {
  try {
    const res: any = await request('/api/call-queue/assignments', {
      method: 'GET',
      params: { status: 'pending', limit: 1 },
    });
    if (typeof res?.pending_count === 'number') return res.pending_count;
    const items: any[] = Array.isArray(res?.assignments) ? res.assignments : [];
    return items.filter((i: any) => i.status === 'pending').length;
  } catch {
    return 0;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/call-queue/assignments on our backend (populated by the sync_call_queue poll).
 * `scope`: 'shared' (default) is the tenant-wide board; 'mine' is the caller's PRIVATE
 * callbacks — the ones they promised at call wrap-up — which never appear in 'shared';
 * 'team' is every CSR's private callbacks (managers, call_board.manage; 403 otherwise).
 * `status` accepts a comma list. Every response also carries `shared_pending_count`
 * and `mine_pending_count`.
 */
export async function getCallQueueAssignments(params?: {
  page?: number;
  limit?: number;
  status?: string;
  scope?: 'shared' | 'mine' | 'team';
  sort?: 'newest' | 'oldest' | 'attempts' | 'due';
}) {
  return request('/api/call-queue/assignments', {
    method: 'GET',
    params,
  });
}

/**
 * One callback row by our backend id, or null when it no longer exists. Used by the
 * post-call prompt to re-check a row's status once the CSR's call leg has dropped.
 */
export async function getCallQueueAssignment(id: number): Promise<any | null> {
  const res: any = await request('/api/call-queue/assignments', {
    method: 'GET',
    params: { id, limit: 1 },
  });
  const items: any[] = Array.isArray(res?.assignments) ? res.assignments : [];
  return items.find((i: any) => Number(i.id) === Number(id)) ?? null;
}

export async function startCallQueueCallback(id: number, data: {
  assigned_to: string | number;
  notes?: string;
  twiml_url?: string;
  twiml_method?: string;
  ongoing_call_sid?: string;
}) {
  console.log('[call-queue] startCallQueueCallback: start', { id, data });
  const res = await request(`/api/call-queue/${id}/start-callback`, {
    baseURL: APPT_CENTER_API_URL,
    method: 'POST',
    data,
    headers: { 'X-Admin-Token': 'admin-1234-OK' },
  });
  console.log('[call-queue] startCallQueueCallback: done', res);
  return res;
}

// ─── Status changes ──────────────────────────────────────────────────────────
// Our DB is the source of truth. A single backend call records the change immediately
// (audit trail captures who/when) AND forwards to Appointment Center server-to-server
// (no browser CORS). The backend returns appt_updated so we know if their leg succeeded.

export type CallbackStatus =
  | 'pending' | 'in_progress' | 'called_back' | 'no_answer' | 'resolved' | 'dismissed';

/**
 * Record a status change on our own backend, keyed by the backend row id (falls back to
 * external_id, seeding the row if the poll hasn't imported it yet). The backend forwards
 * to Appointment Center itself and echoes { appt_updated }.
 */
export async function recordCallbackStatus(payload: {
  id?: number;
  external_id?: number | string | null;
  status: CallbackStatus;
  notes?: string;
  customer_phone?: string;
  reason?: string | null;
  queue_id?: number;
  attempts?: number;
  /** Add `notes` as a new line instead of replacing the row's notes. */
  append_notes?: boolean;
}): Promise<{ success?: boolean; appt_updated?: boolean; changed?: boolean; assignment?: any }> {
  return request('/api/call-queue/update-status', {
    method: 'POST',
    data: payload,
  });
}

/**
 * Change a callback's status. One backend call does both: records locally (source of
 * truth) and forwards to Appointment Center server-side (with csr_id, no CORS).
 * Returns { apptUpdated } so the caller can surface whether the Appt Center leg succeeded.
 */
export async function changeCallbackStatus(
  item: {
    id: number;
    external_id?: string | null;
    customer_phone?: string;
    reason?: string | null;
    attempts?: number;
  },
  status: CallbackStatus,
  _csrId: number | string,
  notes?: string,
  opts?: { appendNotes?: boolean },
): Promise<{ apptUpdated: boolean; changed: boolean }> {
  const res = await recordCallbackStatus({
    id:             item.id,
    external_id:    item.external_id ?? undefined,
    status,
    notes,
    customer_phone: item.customer_phone,
    reason:         item.reason,
    attempts:       item.attempts,
    append_notes:   opts?.appendNotes || undefined,
  });

  // `changed` is false when the backend found nothing to do — e.g. a "no answer" for a
  // row the Twilio customer-leg callback had already put back in the queue.
  return { apptUpdated: res?.appt_updated === true, changed: res?.changed !== false };
}
